import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FeaturedIcon,
  Icons,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import { useNotification } from '../../../hooks/useNotification';
import type { Filing } from '../types';

interface UnlockFilingModalProps {
  isOpen: boolean;
  onClose: () => void;
  filing: Filing;
  onUnlock: (filingId: string) => Promise<boolean>;
  onSuccess?: () => void | Promise<void>;
}

export const UnlockFilingModal = ({
  isOpen,
  onClose,
  filing,
  onUnlock,
  onSuccess,
}: UnlockFilingModalProps) => {
  const { setNotification } = useNotification();
  const isSubmitting = useToggle(false);

  const handleUnlock = async () => {
    isSubmitting.on();
    const success = await onUnlock(filing.id);

    if (!success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to unlock filing.',
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Filing unlocked successfully.',
    });

    try {
      await onSuccess?.();
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to refresh filing after unlock.',
      });
    } finally {
      isSubmitting.off();
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-lg items-start border-b-0 pb-0">
          <FeaturedIcon
            size="lg"
            variant="warning"
            icon={<Icons.Lock01 />}
          />
          <div className="space-y-xs">
            <DialogTitle>Unlock filing?</DialogTitle>
            <DialogDescription>
              This will unlock <span className="font-bold">{filing.filingName}</span>{' '}
              for {filing.corporation.companyDisplayName}.
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} size="md">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUnlock}
            size="md"
            disabled={isSubmitting.value}
          >
            {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
